import { useState, useEffect, useCallback } from "react";

/**
 * Hook to sync filter state with URL query parameters
 * @param {Object} defaultFilters - Default filter values
 * @returns {Object} - { filters, setFilters, updateFilter, resetFilters, getApiParams }
 */
export function useFilterParams(defaultFilters = {}) {
    const getInitialFilters = () => {
        const params = new URLSearchParams(window.location.search);
        const filters = { ...defaultFilters };

        // Parse status array
        const statusParams = params.getAll("status");
        if (statusParams.length > 0) {
            filters.status = statusParams;
        }

        // Parse date filters
        if (params.has("date_from")) {
            filters.date_from = params.get("date_from");
        }
        if (params.has("date_to")) {
            filters.date_to = params.get("date_to");
        }

        // Parse price filters
        if (params.has("price_min")) {
            filters.price_min = params.get("price_min");
        }
        if (params.has("price_max")) {
            filters.price_max = params.get("price_max");
        }

        // Parse search
        if (params.has("search")) {
            filters.search = params.get("search");
        }

        // Parse sort
        if (params.has("sort_by")) {
            filters.sort_by = params.get("sort_by");
        }
        if (params.has("sort_direction")) {
            filters.sort_direction = params.get("sort_direction");
        }

        // Parse group_by
        if (params.has("group_by")) {
            filters.group_by = params.get("group_by");
        }

        // Preserve id param for direct order linking
        if (params.has("id")) {
            filters.id = params.get("id");
        }

        return filters;
    };

    const [filters, setFiltersState] = useState(getInitialFilters);

    // Update URL when filters change
    useEffect(() => {
        const params = new URLSearchParams();

        // Add status array
        if (filters.status && filters.status.length > 0) {
            filters.status.forEach((s) => params.append("status", s));
        }

        // Add date filters
        if (filters.date_from) {
            params.set("date_from", filters.date_from);
        }
        if (filters.date_to) {
            params.set("date_to", filters.date_to);
        }

        // Add price filters
        if (filters.price_min) {
            params.set("price_min", filters.price_min);
        }
        if (filters.price_max) {
            params.set("price_max", filters.price_max);
        }

        // Add search
        if (filters.search) {
            params.set("search", filters.search);
        }

        // Add sort
        if (filters.sort_by) {
            params.set("sort_by", filters.sort_by);
        }
        if (filters.sort_direction) {
            params.set("sort_direction", filters.sort_direction);
        }

        // Add group_by
        if (filters.group_by) {
            params.set("group_by", filters.group_by);
        }

        // Preserve id param
        if (filters.id) {
            params.set("id", filters.id);
        }

        const queryString = params.toString();
        const newUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;

        window.history.replaceState({}, "", newUrl);
    }, [filters]);

    const setFilters = useCallback((newFilters) => {
        setFiltersState((prev) => ({
            ...prev,
            ...newFilters,
        }));
    }, []);

    const updateFilter = useCallback((key, value) => {
        setFiltersState((prev) => ({
            ...prev,
            [key]: value,
        }));
    }, []);

    const resetFilters = useCallback(() => {
        // Preserve the id param when resetting
        const currentId = filters.id;
        const resetState = { ...defaultFilters };
        if (currentId) {
            resetState.id = currentId;
        }
        setFiltersState(resetState);
    }, [defaultFilters, filters.id]);

    // Get params formatted for API call
    const getApiParams = useCallback(() => {
        const params = {};

        if (filters.status && filters.status.length > 0) {
            params.status = filters.status;
        }
        if (filters.date_from) {
            params.date_from = filters.date_from;
        }
        if (filters.date_to) {
            params.date_to = filters.date_to;
        }
        if (filters.price_min) {
            params.price_min = parseFloat(filters.price_min);
        }
        if (filters.price_max) {
            params.price_max = parseFloat(filters.price_max);
        }
        if (filters.search) {
            params.search = filters.search;
        }
        if (filters.sort_by) {
            params.sort_by = filters.sort_by;
        }
        if (filters.sort_direction) {
            params.sort_direction = filters.sort_direction;
        }
        if (filters.group_by) {
            params.group_by = filters.group_by;
        }

        return params;
    }, [filters]);

    return {
        filters,
        setFilters,
        updateFilter,
        resetFilters,
        getApiParams,
    };
}

export default useFilterParams;
